import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

interface IFrameEmbedProps {
  embedCode: string;
}

export const IFrameEmbed = ({ embedCode }: IFrameEmbedProps) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Embed code copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <Card className="p-4">
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold mb-2">HTML iFrame Instructions</h4>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Copy the code below</li>
            <li>Paste it wherever you want the chat interface to appear in your HTML</li>
            <li>Adjust width and height as needed</li>
          </ol>
        </div>

        <div className="relative">
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
            <code>{embedCode}</code>
          </pre>
          <Button
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2"
            onClick={copyToClipboard}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copy Code
              </>
            )}
          </Button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Use this for regular HTML websites. iFrame embeds are static and won't float like the
            JavaScript widget.
          </p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
          <p className="text-sm font-semibold text-amber-800 mb-1">⚠ Important — before you embed</p>
          <p className="text-sm text-amber-700">
            Some websites — especially those served via Google Workspace or similar platforms — restrict iFrame embedding.
            HelpGenie requires microphone access to function. Please confirm your website and hosting provider support both
            iFrame embedding and microphone permissions before going live.
          </p>
        </div>
      </div>
    </Card>
  );
};
